'use client';

import { FiveStarsGold } from 'assets/icons';
import React, { useState, useCallback, useEffect, useRef } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { Container, Content } from './LayoutComponents';
import styles from './FeaturedReviews.module.css';

const CarouselPointer = ({
  base,
  accent,
  className,
  onClick,
}: {
  base: string;
  accent: string;
  className?: string;
  onClick?: () => void;
}) => (
  <button
    className={`${styles.carouselPointer}${className ? ` ${className}` : ''}`}
    style={
      { ['--base']: base || 'black', ['--accent']: accent || 'white' } as React.CSSProperties
    }
    onClick={onClick}
  />
);

export const QuoteCarousel1 = ({
  quotes,
}: {
  className?: string;
  quotes: { quote: string; description: string; author: string }[];
}) => {
  const [currentQuote, setCurrentQuote] = useState(quotes[0]);
  const { quote, description, author } = currentQuote;

  return (
    <div className={styles.quoteCarousel1}>
      <q>{quote}</q>
      <p>
        {author ? <span>{author}, </span> : null}
        {description ? <span>{description}</span> : null}
      </p>
      <div className={styles['carousel-pointers']}>
        {Array(quotes.length)
          .fill(0)
          .map((_, index) => (
            <CarouselPointer
              base="rgba(0, 0, 0, 0.15)"
              accent="rgba(4, 74, 45, 1)"
              className={quote === quotes[index].quote ? styles.active : ''}
              key={index}
              onClick={() => setCurrentQuote(quotes[index])}
            />
          ))}
      </div>
    </div>
  );
};

const QuoteCarousel = ({
  quotes,
}: {
  className?: string;
  quotes: { quote: string; description: React.ReactNode; author: string }[];
}) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const autoplayPlugin = useRef(
    Autoplay({ delay: 3000, stopOnInteraction: false }),
  );

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: true,
      align: 'center',
    },
    [autoplayPlugin.current],
  );

  const [selectedIndex, setSelectedIndex] = useState(0);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  const scrollTo = useCallback(
    (index: number) => {
      if (emblaApi) emblaApi.scrollTo(index);
    },
    [emblaApi],
  );

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi, onSelect]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root || !emblaApi) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          autoplayPlugin.current.play();
        } else {
          autoplayPlugin.current.stop();
        }
      },
      { threshold: 0 },
    );
    observer.observe(root);
    return () => observer.disconnect();
  }, [emblaApi]);

  return (
    <div className={styles.quoteCarousel} ref={rootRef}>
      <div className={styles.embla} ref={emblaRef}>
        <div className={styles.embla__container}>
          {quotes.map((quoteObj, index) => (
            <div key={index} className={styles.embla__slide}>
              <div className={styles['quote-wrapper']}>
                <q>{quoteObj.quote}</q>
                <p>
                  {quoteObj.author}
                  {quoteObj.description ? <>{quoteObj.description}</> : null}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className={styles.embla__dots}>
        {quotes.map((_, index) => (
          <button
            key={index}
            className={`${styles.embla__dot}${
              index === selectedIndex ? ` ${styles['embla__dot--selected']}` : ''
            }`}
            aria-label={`Go to slide ${index + 1}`}
            onClick={() => scrollTo(index)}
          >
            <div className={styles.progress}></div>
          </button>
        ))}
      </div>
    </div>
  );
};

export const FeaturedReviews = ({
  className,
  handleClick,
}: {
  className?: string;
  handleClick: () => void;
}) => {
  const reviews = [
    {
      quote:
        'The API for your body—What an amazing app experience and deep insights into how your body works. This truly goes out to show how each body is different and how nutrition can have an impact on us. Strongly recommend trying it at least once. Your life will never be the same!',
      author: 'ApoorvSharma',
      description: (
        <>
          <span className={styles.bullet}>Verified purchase </span>{' '}
          <span className={styles.bullet}>June 2025</span>
        </>
      ),
    },
    {
      quote:
        'This is a game-changer and has helped me understand multiple aspect of my metabolic health made me conscious about the way I eat and what I eat, also Ultrahuman team is super super responsive and helpful really loving the experience.',
      author: 'AK_AK_AK',
      description: (
        <>
          <span className={styles.bullet}>Verified purchase </span>{' '}
          <span className={styles.bullet}>June 2025</span>
        </>
      ),
    },
    {
      quote:
        'In love with the app. This has to be the best device after Apple Watch which helps track the blood glucose levels, in turn helping maintain a healthy lifestyle by allowing me make the right food choices while staying away from the others. I have quit almost all foods that have added sugar.',
      author: 'Nayanava',
      description: (
        <>
          <span className={styles.bullet}>Verified purchase </span>{' '}
          <span className={styles.bullet}>June 2025</span>
        </>
      ),
    },
  ];

  return (
    <Container
      fill="#F5F5F5"
      className={`${styles.root}${className ? ` ${className}` : ''}`}
    >
      <Content className={styles.content}>
        <div className={styles['description-wrapper']}>
          <div className={styles.description}>
            <p>
              Join <strong className={styles.space}>45,000+ </strong> <br />
              members who have <br />
              <strong> changed their lives</strong>
            </p>
            <FiveStarsGold />
            <button onClick={handleClick}>View all reviews</button>
          </div>
        </div>
        <QuoteCarousel quotes={reviews} />
      </Content>
    </Container>
  );
};
